import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';

import { syncBasePrice } from '../api/option-sync-preview';
import { cellPath, type VariantRowsInput } from '../api/variant-mappers';
import type { VariantRowsForm } from '../hooks/use-variant-rows-form';

import { AddVariantDialog } from './add-variant-dialog';
import { RegenerateSkusDialog } from './regenerate-skus-dialog';
import { SkuHistoryModal } from './sku-history-modal';
import { VariantStatusChip } from './variants-card';

const LINK = 'text-primary h-auto p-0 text-[13px]';

/** A row's status chip that follows its draft. */
export function WatchedVariantStatus({ id }: { id: string }) {
  const { control } = useFormContext<VariantRowsInput>();
  return <VariantStatusChip status={useWatch({ control, name: cellPath(id, 'status') })} />;
}

/** Add variant, Regenerate SKUs and SKU history, with their dialogs: all act on the master. */
export function VariantFooterActions({
  product,
  variants,
}: {
  product: Product;
  variants: readonly Variant[];
}) {
  const { t } = useTranslation('products');
  const [addOpen, setAddOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const hasOptions =
    product.options.length > 0 && product.options.every((option) => option.values.length > 0);
  // An option-less product sells through one default variant; a second one has nothing to differ by.
  const canAdd = hasOptions || variants.length === 0;
  return (
    <span className="inline-flex flex-wrap gap-3.5">
      <Can permission="products.write">
        {canAdd ? (
          <Button
            type="button"
            variant="link"
            size="xs"
            className={LINK}
            onClick={() => setAddOpen(true)}
          >
            {hasOptions ? t('variants.addAction') : t('variants.addDefaultAction')}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="link"
          size="xs"
          className={LINK}
          disabled={variants.length === 0 && !product.skuRule}
          onClick={() => setRegenerateOpen(true)}
        >
          {t('sku.regenerateAction')}
        </Button>
      </Can>
      <Button
        type="button"
        variant="link"
        size="xs"
        className={LINK}
        onClick={() => setHistoryOpen(true)}
      >
        {t('sku.history.action')}
      </Button>
      <RegenerateSkusDialog
        product={product}
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
      />
      <SkuHistoryModal productId={product.id} open={historyOpen} onOpenChange={setHistoryOpen} />
      <AddVariantDialog
        product={product}
        defaultPrice={syncBasePrice(variants)}
        open={addOpen}
        onOpenChange={setAddOpen}
      />
    </span>
  );
}

/** Activate, Archive (drafts) and Delete (after its confirm) for the selected rows. */
export function VariantStatusBulkActions({
  rows,
  selected,
}: {
  rows: VariantRowsForm;
  selected: Variant[];
}) {
  const { t } = useTranslation('products');
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 text-[13px]"
        onClick={() => rows.setStatus(selected, 'active')}
      >
        {t('variants.bulk.activate')}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 text-[13px]"
        onClick={() => rows.setStatus(selected, 'archived')}
      >
        {t('variants.bulk.archive')}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-destructive hover:text-destructive h-8 text-[13px]"
        onClick={() => rows.requestDelete(selected)}
      >
        {t('variants.bulk.delete')}
      </Button>
    </>
  );
}

/** The confirm before deleting variants; `note` says what else it affects where that isn't obvious. */
export function DeleteVariantsDialog({ rows, note }: { rows: VariantRowsForm; note?: string }) {
  const { t } = useTranslation(['products', 'common']);
  const { deleting, removing } = rows;
  const confirm = async () => {
    const failed = await rows.confirmDelete();
    rows.setDeleteError(
      failed.length > 0
        ? t('products:variants.deleteFailed', {
            count: failed.length,
            reasons: failed.map((row) => `${row.title}: ${row.reason}`).join(' · '),
          })
        : null,
    );
  };
  return (
    <AlertDialog open={deleting.length > 0} onOpenChange={(open) => !open && rows.cancelDelete()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('products:variants.deleteMany.title', { count: deleting.length })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {deleting.length === 1
              ? t('products:variants.delete.description', { variant: deleting[0]?.title })
              : t('products:variants.deleteMany.description', { count: deleting.length })}
            {note ? ` ${note}` : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={removing}>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={removing}
            onClick={(event) => {
              event.preventDefault();
              void confirm();
            }}
          >
            {t('common:actions.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
